import React, { useState, useEffect, useCallback } from "react";
import {
  Grid,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableContainer,
  TableBody,
  TextField,
  Pagination,
  Box,
  Typography,
  Fade,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import _ from "lodash";
import { API_URL } from "../../constants";
import * as actions from "./actions";
import { toastError } from "../../utils";
import CustomShowModal from "../../components/Common/CustomShowModal";
import HistoryIcon from "@mui/icons-material/History";
import SearchIcon from "@mui/icons-material/Search";
import SEO from "../../components/SEO";

const PageContainer = styled(Box)({
  minHeight: 'calc(100vh - 64px)',
  background: 'linear-gradient(135deg, #e8eef3 0%, #dce4ec 100%)',
  padding: '32px 24px',
});

const PageHeader = styled(Box)({
  marginBottom: '32px',
  textAlign: 'center',
  '& h1': {
    fontSize: '2rem',
    fontWeight: 700,
    background: 'linear-gradient(135deg, #1a73e8 0%, #00bfa5 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    fontFamily: "'DM Sans', sans-serif",
    margin: 0,
  },
});

const SectionTitle = styled(Typography)({
  fontFamily: "'DM Sans', sans-serif",
  fontSize: '1.5rem',
  fontWeight: 700,
  color: '#202124',
  marginBottom: '24px',
});

const SearchField = styled(TextField)({
  fontFamily: "'DM Sans', sans-serif",
  "& .MuiOutlinedInput-root": {
    borderRadius: '12px',
    backgroundColor: '#ffffff',
    fontFamily: "'DM Sans', sans-serif",
    "& fieldset": {
      borderColor: '#e0e0e0',
      borderWidth: '2px',
    },
    "&:hover fieldset": {
      borderColor: '#1a73e8',
    },
    "&.Mui-focused fieldset": {
      borderColor: "#1a73e8",
      borderWidth: "2px",
    },
  },
  "& .MuiInputBase-input": {
    color: '#202124',
    fontFamily: "'DM Sans', sans-serif",
  },
});

const StyledTableContainer = styled(TableContainer)({
  borderRadius: '16px',
  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
  backgroundColor: 'white',
  overflow: 'hidden',
  marginTop: '24px',
});

const StyledTableHead = styled(TableHead)({
  background: 'linear-gradient(135deg, #1a73e8 0%, #00bfa5 100%)',
  '& .MuiTableCell-head': {
    color: 'white',
    fontWeight: 700,
    fontSize: '0.95rem',
    fontFamily: "'DM Sans', sans-serif",
    borderBottom: 'none',
    padding: '16px',
  },
});

const StyledTableRow = styled(TableRow)({
  '&:nth-of-type(odd)': {
    backgroundColor: '#f8f9fa',
  },
  '&:hover': {
    backgroundColor: '#e3f2fd',
    transition: 'all 0.2s ease',
  },
  '& .MuiTableCell-root': {
    fontFamily: "'DM Sans', sans-serif",
    fontSize: '0.9rem',
    color: '#202124',
    borderBottom: '1px solid #e0e0e0',
    padding: '14px 16px',
  },
});

function Promotions(props) {
  const [promotions, setPromotions] = useState([]);
  const [count, setCount] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  const [filters, setFilters] = useState({
    limit: 15,
    offset: 0,
    page: 1,
    search: "",
  });
  let totalPages = Math.ceil(Number(count) / filters.limit);

  const delaySearch = useCallback(
    _.debounce((filters) => onGetPromotions(filters), 500),
    []
  );

  const onPageChange = (event, page) => {
    const offset = (page - 1) * filters.limit;
    const newFilters = { ...filters, page, offset };
    setFilters(newFilters);
    onGetPromotions(newFilters);
  };

  const onGetPromotions = (filters) => {
    props.setLoader(true);
    actions
      .getCreatedPromotions(filters)
      .then((res) => {
        props.setLoader(false);
        setPromotions(res.body);
        setCount(res.total);
      })
      .catch((err) => {
        props.setLoader(false);
        toastError(err.message);
      });
  };

  const onChangeSearch = (e) => {
    const newFilters = { ...filters, search: e.target.value };
    setFilters(newFilters);
    delaySearch(newFilters);
  };

  const handleOpenModal = (image) => {
    setSelectedImage(`${API_URL}/${image}`);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedImage(null);
  };

  useEffect(() => {
    onGetPromotions(filters);
  }, []);

  return (
    <>
    <SEO
      title="Promotions"
      description="View and manage your promotional campaigns and marketing activities for your music releases."
    />
    <PageContainer>
      <Fade in timeout={800}>
        <Box>
          <PageHeader>
            <Typography variant="h1">Promotions</Typography>
          </PageHeader>

          <Grid container>
            <Grid item xs={12}>
              <SectionTitle>
                <HistoryIcon sx={{ color: '#1a73e8', fontSize: 28, mr: 1, verticalAlign: 'text-bottom' }} />
                Your Promotions History
              </SectionTitle>
            </Grid>

            <Grid item xs={12} sm={12} md={6} lg={4} sx={{ mb: 3 }}>
              <SearchField
                type="text"
                variant="outlined"
                size="small"
                onChange={onChangeSearch}
                value={filters.search}
                fullWidth
                placeholder="Search promotions..."
                InputProps={{
                  startAdornment: <SearchIcon sx={{ color: '#5f6368', mr: 1 }} />,
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <StyledTableContainer component={Paper}>
                <Table>
                  <StyledTableHead>
              <TableRow>
                <TableCell align="center">No.</TableCell>
                <TableCell>Song Name</TableCell>
                <TableCell>Platform</TableCell>
                <TableCell>Promotion Type</TableCell>
                <TableCell>Playlist Name</TableCell>
                <TableCell>Playlist Link</TableCell>
                <TableCell align="center">Image</TableCell>
                <TableCell>Created On</TableCell>
              </TableRow>
            </StyledTableHead>

            <TableBody>
              {promotions?.length > 0 ? (
                promotions?.map((item, key) => (
                  <StyledTableRow key={key}>
                    <TableCell align="center">
                      {(filters.page - 1) * filters.limit + (key + 1)}
                    </TableCell>
                    <TableCell>{item.song}</TableCell>
                    <TableCell>{item.platform}</TableCell>
                    <TableCell>{item.type}</TableCell>
                    <TableCell>{item.playlist_name}</TableCell>
                    <TableCell>
                      <a
                        href={item.playlist_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: "#1a73e8",
                          fontWeight: 600,
                          fontFamily: "'DM Sans', sans-serif",
                          textDecoration: "none",
                        }}
                        onMouseOver={(e) => e.target.style.textDecoration = "underline"}
                        onMouseOut={(e) => e.target.style.textDecoration = "none"}
                      >
                        Click here
                      </a>
                    </TableCell>
                    <TableCell align="left" style={{ padding: 4 }}>
                      <div
                        className="album-artwork-catalog"
                        style={{ cursor: "pointer" }}
                        onClick={() => handleOpenModal(item.image)}
                      >
                        <img
                          src={`${API_URL}/${item.image}`}
                          height="60px"
                          alt="Promotion artwork"
                          onError={(e) => {
                            // Remove error handler to prevent infinite loop
                            e.target.onerror = null;
                            // Use a data URI as fallback (no server request needed)
                            e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgZmlsbD0iI2UwZTBlMCIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTQiIGZpbGw9IiM5OTk5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5ObyBJbWFnZTwvdGV4dD48L3N2Zz4=';
                          }}
                        />
                      </div>
                    </TableCell>

                    <TableCell>{item.created_at}</TableCell>
                  </StyledTableRow>
                ))
              ) : (
                <StyledTableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography sx={{ fontWeight: 600, fontSize: "1.1rem", color: '#5f6368', py: 3 }}>
                      No Promotions Yet
                    </Typography>
                  </TableCell>
                </StyledTableRow>
              )}
            </TableBody>
          </Table>
          <Box sx={{ display: 'flex', justifyContent: 'center', padding: '16px' }}>
            <Pagination
              count={totalPages}
              showFirstButton
              showLastButton
              onChange={onPageChange}
              page={filters.page || 0}
              sx={{
                '& .MuiPaginationItem-root': {
                  fontFamily: "'DM Sans', sans-serif",
                  fontWeight: 600,
                  color: '#1a73e8',
                },
                '& .Mui-selected': {
                  background: 'linear-gradient(135deg, #1a73e8 0%, #00bfa5 100%)',
                  color: 'white',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1557b0 0%, #009688 100%)',
                  },
                },
              }}
            />
          </Box>
        </StyledTableContainer>
            </Grid>
          </Grid>
        </Box>
      </Fade>
    </PageContainer>
      <CustomShowModal
        open={modalOpen}
        handleClose={handleCloseModal}
        maxWidth="md"
        title="Preview Image"
      >
        <div style={{ textAlign: "center" }}>
          {selectedImage ? (
            <img
              src={selectedImage}
              alt="Preview"
              style={{ maxWidth: "100%", maxHeight: "500px" }}
              onError={(e) => {
                // Remove error handler to prevent infinite loop
                e.target.onerror = null;
                // Use a data URI as fallback (no server request needed)
                e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgZmlsbD0iI2UwZTBlMCIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTQiIGZpbGw9IiM5OTk5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5ObyBJbWFnZTwvdGV4dD48L3N2Zz4=';
              }}
            />
          ) : (
            "No image selected."
          )}
        </div>
      </CustomShowModal>
    </>
  );
}

export default Promotions;
